/**
 * SchoolsDirectoryPage — the public list of schools at /schools.
 * Reads `GET /api/public/school/directory`; each card opens that school's page.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, GraduationCap, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  kindergarten: 'Kindergarten', primary: 'Primary school', secondary: 'Secondary school',
  vocational: 'Vocational college', college: 'College', university: 'University',
  bible_college: 'Bible college',
};

export default function SchoolsDirectoryPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['public-school-directory'],
    queryFn: () => fetch(`${API_URL}/api/public/school/directory`).then((r) => r.json()),
    staleTime: 120000,
  });
  const schools = data?.schools || [];

  return (
    <div className="min-h-screen bg-[#FAF7F1] text-[#14110F]" data-testid="schools-directory">
      <header className="border-b border-black/[0.08]">
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10 py-6">
          <Link to="/" className="text-xs text-black/50 hover:text-black inline-flex items-center gap-1.5"
                data-testid="schools-home-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 lg:px-10 py-16">
        <p className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#B4881F]">Education</p>
        <h1 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight"
            style={{ fontFamily: "'Newsreader', Georgia, serif" }}>
          Our schools and colleges
        </h1>
        <p className="mt-5 max-w-xl text-black/55">
          From kindergarten to university — every campus runs on the same admissions,
          academic and finance spine.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
             data-testid="schools-grid">
          {isLoading && <p className="p-2 text-sm text-black/40">Loading…</p>}
          {!isLoading && schools.length === 0 && (
            <p className="p-2 text-sm text-black/40" data-testid="schools-empty">
              No school has published a page yet.
            </p>
          )}
          {schools.map((s) => (
            <button key={s.id} onClick={() => navigate(`/school/${s.id}/website`)}
                    data-testid={`school-card-${s.id}`}
                    className="group text-left bg-white border border-black/[0.08] p-8 hover:border-black/20 transition-colors">
              <div className="flex items-center gap-3">
                {s.logo_url
                  ? <img src={s.logo_url} alt={s.name} className="h-9 w-9 object-contain" />
                  : <GraduationCap className="h-5 w-5 text-[#B4881F]" />}
                {s.type && (
                  <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-black/40">
                    {TYPE_LABELS[s.type] || s.type}
                  </span>
                )}
              </div>
              <h2 className="mt-5 text-xl font-semibold leading-snug"
                  style={{ fontFamily: "'Newsreader', Georgia, serif" }}>{s.name}</h2>
              {s.tagline && <p className="mt-3 text-sm text-black/50 line-clamp-2">{s.tagline}</p>}
              <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-black/40">
                {s.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.city}</span>}
                {s.programmes > 0 && <span>{s.programmes} programmes</span>}
                {s.open_intakes > 0 && <span className="text-[#B4881F] font-semibold">Applications open</span>}
              </p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-[#B4881F]">
                Visit the school <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
              </span>
            </button>
          ))}
        </div>

        <div className="mt-16">
          <Button variant="outline" onClick={() => navigate('/')}
                  data-testid="schools-back-btn"
                  className="rounded-none border-black/15 bg-transparent hover:bg-black/[0.03]">
            Back to the homepage
          </Button>
        </div>
      </main>
    </div>
  );
}
